import { EditableTableCell } from 'components/EditableTableCell';
import { useRequest } from 'features/request/useRequest';
import { WorkOrderStatusEnum, ContractStatusEnum, WorkOrderPriceTypeEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { gqlUpdateSeasonVolume } from '../workOrderQueries';

interface IWorkOrderPriceVolumeEditProps {
    workOrderSeasonId: number;
    workOrderPriceId: number;
    defaultValue: string;
    type: WorkOrderPriceTypeEnum | undefined | null;
    onChange: (reloadRowsIds?: number[] | null) => void;
    contractStatus?: ContractStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    toolTipValue?: string | null;
    isDisabled: boolean;
}

export const WorkOrderPriceVolumeEdit = memo(
    ({
        workOrderStatus,
        workOrderSeasonId,
        workOrderPriceId,
        defaultValue,
        onChange,
        type,
        contractStatus,
        toolTipValue,
        isDisabled,
    }: IWorkOrderPriceVolumeEditProps) => {
        const dispatch = useDispatch();

        const [
            updateSeasonVolumeResponse,
            updateSeasonVolumeResponseStatus,
            updateSeasonVolumeResponseError,
            updateSeasonVolume,
        ] = useRequest({
            requestId: `updateSeasonVolume_${workOrderSeasonId}_${workOrderPriceId}`,
            query: gqlUpdateSeasonVolume,
            withEffect: false,
        });

        const isContractStatusWork = contractStatus === ContractStatusEnum.InImplementation;
        const isStatusWorkOrderDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
        const isStatusWorkOrderCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

        const disabled = useMemo(
            () =>
                (type &&
                    [
                        WorkOrderPriceTypeEnum.ConstraintCoefficient,
                        WorkOrderPriceTypeEnum.Coefficient,
                        WorkOrderPriceTypeEnum.TechPartCoefficient,
                        WorkOrderPriceTypeEnum.DismantlingCoefficient,
                    ].includes(type)) ||
                !isContractStatusWork ||
                isStatusWorkOrderCompleted ||
                isStatusWorkOrderDelayed ||
                isDisabled,
            [isContractStatusWork, isDisabled, isStatusWorkOrderCompleted, isStatusWorkOrderDelayed, type],
        );

        const updateVolumeHandler = useCallback(
            (value: string) => {
                dispatch(
                    updateSeasonVolume({
                        workOrderSeasonId,
                        workOrderPriceId,
                        volume: replacerCost(value),
                    }),
                );
            },
            [dispatch, updateSeasonVolume, workOrderPriceId, workOrderSeasonId],
        );

        return (
            <EditableTableCell
                decimalScale={3}
                disabled={disabled}
                fieldValue={defaultValue}
                reloadHandler={onChange}
                updateRequest={updateVolumeHandler}
                updateStatus={updateSeasonVolumeResponseStatus}
                requestResultObj={updateSeasonVolumeResponse?.uerUpdateSeasonVolume}
                requestErrors={updateSeasonVolumeResponseError}
                errorModalTitle="Редактирование Объёма"
                toolTipValue={toolTipValue}
            />
        );
    },
);

WorkOrderPriceVolumeEdit.displayName = 'WorkOrderPriceVolumeEdit';
